/**
 * @file   src\components\GiftCardClaimOtp.tsx
 * @brief  Gift card claim OTP popup.
 * @date   OCT, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import { React, useEffect, useState, Button, Link, Offcanvas } from "../utils/thirdpartyComponents";
import Verifyicon from "../assets/images/verify-img.svg";
import CWInput from "src/components/CWInput";
import "src/components/OffCanvas.scss";
import axios from "src/config/axiosConfig";
import { GiftCardApi } from "src/utils/apiUrls";
import { CommonMessages } from "src/messages/validationMessages";
import { MessageToaster } from "src/utils/toastUtil";
import SmallLoader from "src/components/SmallLoader";

const OTP_LENGTH = 4;
const GIFT_CARD_SUCCESS = 1000;
const CLAIM_OTP_INVALID = 1045;
const CLAIM_OTP_LOCKED = 1046;

interface GiftCardClaimOtpProps {
  show: boolean;
  giftCardCode: string;
  onHide: () => void;
  onClaimed: () => void;
}

const claimStatus = (data: any) => Number(data?.statusCode ?? data?.error ?? 0);

const claimSucceeded = (data: any) => data?.error === 0 || data?.statusCode === GIFT_CARD_SUCCESS;

const errorMessage = (error: any, fallback: string) =>
  error?.data?.message || error?.response?.data?.message || error?.message || fallback;

const GiftCardClaimOtp = ({ show, giftCardCode, onHide, onClaimed }: GiftCardClaimOtpProps) => {
  const toaster = new MessageToaster();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(30);
  const [showResend, setShowResend] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimer(30);
    setShowResend(false);
    setError("");
    setLocked(false);
  }, [show, giftCardCode]);

  useEffect(() => {
    if (!show || showResend) {
      return;
    }
    const interval = window.setInterval(() => {
      setTimer((current) => {
        if (current <= 1) {
          window.clearInterval(interval);
          setShowResend(true);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [show, showResend]);

  const focusInput = (index: number) => {
    const input = document.getElementById(`gift-otp-${index + 1}`) as HTMLInputElement | null;
    input?.focus();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setError("");
    const digits = event.target.value.replace(/\D/g, "");
    if (!digits) {
      return;
    }
    if (digits.length >= OTP_LENGTH) {
      setOtp(digits.slice(0, OTP_LENGTH).split(""));
      focusInput(OTP_LENGTH - 1);
      return;
    }
    const digit = digits.slice(-1);
    setOtp((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
    if (index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Enter") {
      handleVerify();
      return;
    }
    if (event.key !== "Backspace" && event.key !== "Delete") {
      return;
    }
    event.preventDefault();
    if (!otp[index] && index > 0) {
      focusInput(index - 1);
      return;
    }
    setOtp((current) => {
      const next = [...current];
      next[index] = "";
      return next;
    });
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData.getData("text").replace(/\D/g, "");
    if (pasteData.length < OTP_LENGTH) {
      return;
    }
    event.preventDefault();
    setOtp(pasteData.slice(0, OTP_LENGTH).split(""));
    focusInput(OTP_LENGTH - 1);
  };

  const resendCode = async () => {
    if (locked || loading) {
      return;
    }
    const locationId = Number(localStorage.getItem("selectedLocation") || 0);
    try {
      setLoading(true);
      const { data } = await axios.post(GiftCardApi.CLAIM_START, {
        locationId,
        giftCardNumber: giftCardCode,
      });
      if (claimSucceeded(data)) {
        setOtp(Array(OTP_LENGTH).fill(""));
        setError("");
        setTimer(30);
        setShowResend(false);
        toaster.toastSuccess(data?.message || "Verification code sent.");
      } else {
        const status = claimStatus(data);
        setError(data?.message || "Unable to resend the verification code.");
        if (status === CLAIM_OTP_LOCKED) {
          setLocked(true);
        }
      }
    } catch (requestError: any) {
      const status = claimStatus(requestError?.data || requestError?.response?.data);
      setError(errorMessage(requestError, "Unable to resend the verification code."));
      if (status === CLAIM_OTP_LOCKED) {
        setLocked(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (locked || loading) {
      return;
    }
    if (otp.some((digit) => !digit)) {
      setError(CommonMessages.otp_required);
      return;
    }
    const locationId = Number(localStorage.getItem("selectedLocation") || 0);
    if (!locationId) {
      setError("Select a location.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(GiftCardApi.CLAIM_CONFIRM, {
        locationId,
        giftCardNumber: giftCardCode,
        otp: otp.join(""),
      });
      if (!data) {
        return;
      }
      if (claimSucceeded(data)) {
        toaster.toastSuccess(data?.message || "Gift card added to your wallet.");
        onClaimed();
        onHide();
        return;
      }
      const status = claimStatus(data);
      setError(data?.message || (status === CLAIM_OTP_INVALID ? "Invalid verification code." : "Unable to claim the gift card."));
      if (status === CLAIM_OTP_LOCKED) {
        setLocked(true);
      }
    } catch (requestError: any) {
      const body = requestError?.data || requestError?.response?.data;
      const status = claimStatus(body);
      setError(errorMessage(requestError, status === CLAIM_OTP_INVALID ? "Invalid verification code." : "Unable to claim the gift card."));
      if (status === CLAIM_OTP_LOCKED) {
        setLocked(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Offcanvas id="verify-offcanvas" show={show} onHide={onHide} placement="bottom">
      <Offcanvas.Header closeButton />
      <Offcanvas.Body>
        <div className="mobile-icon text-center">
          <img src={Verifyicon} alt="Car Wash App" />
        </div>
        <h1>Verification</h1>
        <p>Enter the verification code we just sent to your email address.</p>
        <div className="otp-wrap d-flex justify-content-center">
          {otp.map((value, index) => (
            <CWInput
              key={index}
              id={`gift-otp-${index + 1}`}
              autoFocus={index === 0}
              name="otpinput"
              type="text"
              inputmode="numeric"
              pattern="\d*"
              errorMessage=""
              maxLength={OTP_LENGTH}
              value={value}
              onChange={(event) => handleChange(event, index)}
              onKeyPress={(event) => handleKeyDown(event, index)}
              onPaste={handlePaste}
            />
          ))}
        </div>
        <div className="control-group text-center">
          <div className="timer">{`00:${timer.toString().padStart(2, "0")}`}</div>
          {!showResend || locked ? (
            <span className="code-link">Resend Code</span>
          ) : (
            <Link
              to=""
              title="Resend Code"
              className="active"
              onClick={(event) => {
                event.preventDefault();
                resendCode();
              }}
            >
              Resend Code
            </Link>
          )}
        </div>
      </Offcanvas.Body>
      <div className="offcanvas-footer">
        {error ? <span className="error-msg">{error}</span> : null}
        <Button variant="primary" className="primary-btn btn-main" onClick={handleVerify} disabled={locked || loading}>
          Verify
        </Button>
      </div>
      {loading ? <SmallLoader /> : null}
    </Offcanvas>
  );
};

export default GiftCardClaimOtp;
